import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { CheckCircle2, RefreshCw, WifiOff } from "lucide-react";
import { useLanguage } from "../Context/LanguageContext";
import { getLastLiveMessageAt, getLiveStatus, subscribeLiveStatus } from "../utils/liveConnection";

/** Short reconnects (page switch, socket replacement) finish well within this and stay invisible. */
const SHOW_DELAY_MS = 3000;
const RECOVERED_MS = 2500;

const TONES = {
  connecting: "border-amber-400/40 bg-amber-50 text-amber-800 dark:bg-[#2a2210] dark:text-amber-200",
  reconnecting: "border-amber-400/40 bg-amber-50 text-amber-800 dark:bg-[#2a2210] dark:text-amber-200",
  offline: "border-red-400/40 bg-red-50 text-red-700 dark:bg-[#2a1216] dark:text-red-200",
  recovered: "border-emerald-400/40 bg-emerald-50 text-emerald-700 dark:bg-[#0f2a1e] dark:text-emerald-200",
};

const formatTime = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

/** Tells the user when live data is not flowing, so stale values never look like a frozen UI. */
export default function LiveStatusChip() {
  const status = useSyncExternalStore(subscribeLiveStatus, getLiveStatus);
  const { t } = useLanguage();
  const [shown, setShown] = useState(null);
  const problemShown = useRef(false);

  useEffect(() => {
    if (status === "live" || status === "idle") {
      if (status === "live" && problemShown.current) {
        problemShown.current = false;
        setShown("recovered");
        const id = window.setTimeout(() => setShown(null), RECOVERED_MS);
        return () => window.clearTimeout(id);
      }
      problemShown.current = false;
      setShown(null);
      return undefined;
    }
    const delay = status === "offline" || problemShown.current ? 0 : SHOW_DELAY_MS;
    const id = window.setTimeout(() => {
      problemShown.current = true;
      setShown(status);
    }, delay);
    return () => window.clearTimeout(id);
  }, [status]);

  if (!shown) return null;

  const lastAt = getLastLiveMessageAt();
  const Icon = shown === "offline" ? WifiOff : shown === "recovered" ? CheckCircle2 : RefreshCw;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-none fixed left-1/2 top-3 z-[1400] flex -translate-x-1/2 items-center gap-2 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold shadow-lg ${TONES[shown]}`}
    >
      <Icon size={14} className={shown === "reconnecting" || shown === "connecting" ? "animate-spin" : ""} />
      <span>{t(`connection.${shown}`)}</span>
      {shown !== "recovered" && shown !== "connecting" && lastAt ? (
        <span className="font-medium opacity-75">
          · {t("connection.lastUpdate")} {formatTime(lastAt)}
        </span>
      ) : null}
    </div>
  );
}
